import { Box, Stack, Typography } from '@mui/material';
import { East } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { IFeatureGoalPath } from '../data/types';
import { colors, reducedMotion } from '../styles';

const finaleGlow = keyframes`
    0%, 100% { box-shadow: 0 6px 16px rgba(16, 185, 129, 0.35); }
    50% { box-shadow: 0 8px 26px rgba(5, 150, 105, 0.55); }
`;

const STEP_STYLES = [
    { color: '#155e75', bg: 'linear-gradient(180deg, #f0fdfa, #a5f3fc)', border: 'rgba(6, 182, 212, 0.35)' },
    { color: '#075985', bg: 'linear-gradient(180deg, #e0f2fe, #7dd3fc)', border: 'rgba(14, 165, 233, 0.42)' },
    { color: '#1e40af', bg: 'linear-gradient(180deg, #dbeafe, #60a5fa)', border: 'rgba(37, 99, 235, 0.45)' },
    { color: '#713f12', bg: 'linear-gradient(135deg, #fef08a 0%, #fbbf24 48%, #f59e0b 100%)', border: 'rgba(245, 158, 11, 0.55)' }
];

const FINALE_STYLE = {
    color: '#065f46',
    bg: 'linear-gradient(135deg, #6ee7b7, #10b981, #047857)',
    border: 'rgba(16, 185, 129, 0.6)'
};

interface GoalPathChipsProps {
    path: IFeatureGoalPath;
    size?: 'compact' | 'large';
}

const GoalPathChips = ({ path, size = 'compact' }: GoalPathChipsProps) => {
    const large = size === 'large';
    const earlySteps = path.steps.slice(0, -1);
    const finale = path.steps[path.steps.length - 1];

    return (
        <Stack spacing={1} sx={{ alignItems: 'flex-start', width: '100%' }}>
            <Typography
                sx={{
                    fontWeight: 900,
                    fontSize: large ? 16 : 13,
                    color: colors.ink,
                    lineHeight: 1.2
                }}
            >
                {path.label ?? 'המטרה'}
            </Typography>
            <Box
                dir="ltr"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: large ? 1 : 0.75,
                    minWidth: 0
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: large ? 1 : 0.65,
                        rowGap: 0.85,
                        flex: '0 1 auto'
                    }}
                >
                    {earlySteps.map((step, index) => {
                        const style = STEP_STYLES[Math.min(index, STEP_STYLES.length - 1)];
                        return (
                            <Box
                                key={`${step}-${index}`}
                                sx={{ display: 'flex', alignItems: 'center', gap: large ? 1 : 0.65 }}
                            >
                                {index > 0 && <East sx={{ fontSize: large ? 18 : 14, color: '#38bdf8' }} />}
                                <Box
                                    sx={{
                                        px: large ? 1.85 : 1.3,
                                        py: large ? 0.7 : 0.42,
                                        borderRadius: '999px',
                                        fontWeight: 900,
                                        fontSize: large ? 15 : 13,
                                        lineHeight: 1.2,
                                        color: style.color,
                                        background: style.bg,
                                        border: `1px solid ${style.border}`,
                                        boxShadow: '0 4px 10px rgba(14, 165, 233, 0.16)'
                                    }}
                                >
                                    {step}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
                {finale && (
                    <>
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: large ? 40 : 28,
                                alignSelf: 'center'
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    height: large ? 6 : 5,
                                    borderRadius: 99,
                                    background: 'linear-gradient(90deg, #38bdf8, #10b981)'
                                }}
                            />
                            <East
                                sx={{
                                    fontSize: large ? 38 : 30,
                                    color: '#10b981',
                                    ml: -0.75,
                                    flexShrink: 0
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: large ? 2.1 : 1.55,
                                py: large ? 0.85 : 0.5,
                                borderRadius: '999px',
                                fontWeight: 900,
                                fontSize: large ? 16 : 13,
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                color: FINALE_STYLE.color,
                                background: FINALE_STYLE.bg,
                                border: `1px solid ${FINALE_STYLE.border}`,
                                boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
                                animation: `${finaleGlow} 2.4s ease-in-out infinite`,
                                [reducedMotion]: { animation: 'none' }
                            }}
                        >
                            {finale}
                        </Box>
                    </>
                )}
            </Box>
            {path.caption && (
                <Typography
                    sx={{
                        color: colors.body,
                        fontSize: large ? 15 : 13,
                        lineHeight: 1.65,
                        fontWeight: 600
                    }}
                >
                    {path.caption}
                </Typography>
            )}
        </Stack>
    );
};

export default GoalPathChips;
